import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import type { StyleSpecification } from "maplibre-gl";
import { Layers3, Map as MapIcon, Mountain } from "lucide-react";
import { FetchSource, FileSource, PMTiles, type Header } from "pmtiles";
import { getAccessToken } from "../lib/supabase";
import {
  currentUserCanRenderDerivedLayer,
  getActiveDerivedLayerRelease,
  getDerivedLayerMapAccess,
  probeActiveDerivedLayerAvailability,
  THERA_DERIVED_LAYER_RENDERING_ENABLED,
} from "../lib/derived-layer-release";
import {
  derivedLayerOfflineRuntimeEnabled,
  downloadDerivedLayerForOffline,
  getStoredDerivedLayerForCurrentSession,
  type StoredDerivedLayerRelease,
} from "../lib/derived-layer-offline";
import { contourFileFor, downloadMapPack, getStoredMapPack, terrainFileFor } from "../lib/offline-maps";
import type { TrackPoint } from "../lib/offline-draft";
import type { RouteGeoJson } from "../lib/route-workflow";

const archives = new Map<string, PMTiles>();
let protocolRegistered = false;
let contourWebProtocolRegistered = false;
const contourWebArchives = new Map<string, PMTiles>();
let archiveSequence = 0;
type LayerMode = "map" | "natural" | "relief" | "3d" | "outdoor";

// ETAPA 3 — THERA Topografico 3D.
// Reutiliza el mismo raster DEM regional que Natural 3D. El color-relief se
// calcula en el dispositivo a partir de la elevacion real del DEM, por lo que
// no añade un segundo paquete raster ni inventa cotas.
const TOPO_COLOR_RELIEF_PAINT = {
  "color-relief-color": [
    "interpolate",
    ["linear"],
    ["elevation"],
    0, "#477f58",
    1000, "#5f8d55",
    1800, "#75975a",
    2000, "#809d5c",
    2500, "#94a45e",
    3000, "#a6a765",
    3200, "#b2a86a",
    3400, "#bdaa72",
    3600, "#c4a874",
    3700, "#c6a06f",
    3900, "#c09669",
    4000, "#b98d65",
    4150, "#ae8262",
    4300, "#a1765f",
    4450, "#946f61",
    4600, "#866c65",
    4750, "#7c716c",
    4800, "#797570",
    5000, "#979893",
    5150, "#adaeaa",
    5300, "#c4c5c1",
    5500, "#d5d6d2",
    5700, "#e5e6e2",
    6000, "#eff0ed",
    6300, "#f7f8f5"
  ],
  "color-relief-opacity": 0.76,
} as const;

const NATURAL_HILLSHADE = {
  "hillshade-exaggeration": 0.78,
  "hillshade-shadow-color": "#263b31",
  "hillshade-highlight-color": "#f6f0dc",
  "hillshade-accent-color": "#6d7a67",
} as const;

const TOPO_HILLSHADE = {
  "hillshade-exaggeration": 0.60,
  "hillshade-shadow-color": "#3f3931",
  "hillshade-highlight-color": "#fbf5e7",
  "hillshade-accent-color": "#756957",
} as const;

const OUTDOOR_HILLSHADE = {
  "hillshade-exaggeration": 0.82,
  "hillshade-shadow-color": "#29382f",
  "hillshade-highlight-color": "#f5f0df",
  "hillshade-accent-color": "#687260",
} as const;

// THERA MAP ENGINE — Motor Cartografico Regional 4.2.
// Natural, Topografico y Outdoor se activan automaticamente para cualquier
// paquete regional *-detallado.pmtiles. No requiere condiciones por provincia.
// No agrega mapas, no cambia R2/IndexedDB y no toca GPS, rutas ni navegación.
const NATURAL_COLOR_RELIEF_PAINT = {
  "color-relief-color": [
    "interpolate", ["linear"], ["elevation"],
    0, "#47745a", 1000, "#5e835f", 1800, "#78916a",
    2400, "#8f9c72", 2900, "#a1a477", 3200, "#aca57a",
    3500, "#b2a17b", 3800, "#aa9472", 4100, "#9c846d",
    4400, "#88756b", 4700, "#77746f", 5000, "#92938e",
    5300, "#b4b5b0", 5600, "#d1d2ce", 6000, "#ecece8"
  ],
  "color-relief-opacity": 0.30,
} as const;

const OUTDOOR_COLOR_RELIEF_PAINT = {
  "color-relief-color": [
    "interpolate", ["linear"], ["elevation"],
    0, "#416f55", 1000, "#59805a", 1800, "#718d61",
    2300, "#859867", 2800, "#98a06b", 3100, "#a4a36c",
    3350, "#aaa06a", 3550, "#ad9b68", 3750, "#aa9365",
    3950, "#a18863", 4150, "#967e62", 4400, "#887565",
    4650, "#79716b", 4900, "#83827d", 5150, "#a2a39e",
    5400, "#c0c1bd", 5700, "#dadbd7", 6000, "#eeeeeb"
  ],
  "color-relief-opacity": 0.38,
} as const;

function isTheraRegional3DFile(file: string) {
  // El mapa nacional se mantiene ligero. Todo paquete regional detallado usa
  // el motor 3D y adopta automaticamente su DEM/curvas por nomenclatura.
  return file.endsWith("-detallado.pmtiles");
}

// Outdoor 3D nacional: disponible UNICAMENTE en el visor web.
// Se excluyen explicitamente Capacitor/Android y los shells localhost usados
// por la app nativa, para no cambiar descargas, offline ni interfaz movil.
function isEcuadorEssentialWeb(file: string) {
  const nativeShell =
    (window as any).Capacitor?.isNativePlatform?.() === true ||
    window.location.protocol === "capacitor:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  return file === "ecuador-esencial.pmtiles" && !nativeShell;
}

function safePaint(map: maplibregl.Map, layerId: string, property: string, value: unknown) {
  if (!map.getLayer(layerId)) return;
  try { (map as any).setPaintProperty(layerId, property, value); } catch { /* estilo opcional */ }
}

function safeLayout(map: maplibregl.Map, layerId: string, property: string, value: unknown) {
  if (!map.getLayer(layerId)) return;
  try { (map as any).setLayoutProperty(layerId, property, value); } catch { /* estilo opcional */ }
}

function applyRegionalVectorProfile(map: maplibregl.Map, mode: LayerMode) {
  const topographic = mode === "relief";
  const outdoor = mode === "3d";
  const natural = mode === "natural";

  // Terreno y ecosistema por encima de la red vial.
  safePaint(map, "landcover", "fill-opacity", topographic ? 0.36 : natural ? 0.66 : outdoor ? 0.52 : 0.58);
  safePaint(map, "landuse", "fill-opacity", topographic ? 0.18 : natural ? 0.30 : outdoor ? 0.24 : 0.28);
  safePaint(map, "water", "fill-color", "#5ca8c1");
  safePaint(map, "water", "fill-opacity", 0.90);
  safePaint(map, "water-lines", "line-color", "#3f93ad");
  safePaint(map, "water-lines", "line-opacity", topographic ? 0.88 : 0.80);

  // Paleta vial Outdoor: piedra/marfil/tierra. Evita el blanco dominante.
  safePaint(map, "road-casing", "line-color", "#756f63");
  safePaint(map, "road-casing", "line-opacity", ["interpolate", ["linear"], ["zoom"], 7, 0.18, 10, 0.28, 13, topographic ? 0.46 : outdoor ? 0.34 : 0.38, 16, topographic ? 0.58 : 0.46]);
  safePaint(map, "roads", "line-color", [
    "match", ["get", "kind"],
    "highway", "#c9c2b1",
    "major_road", "#c1baa9",
    "medium_road", "#b5af9f",
    "minor_road", "#aaa596",
    "#9d9584"
  ]);
  safePaint(map, "roads", "line-opacity", ["interpolate", ["linear"], ["zoom"], 7, 0.22, 10, 0.40, 12, 0.55, 14, 0.70, 16, 0.84]);
  safePaint(map, "trails", "line-color", "#806f5e");
  safePaint(map, "trails", "line-opacity", ["interpolate", ["linear"], ["zoom"], 10, 0.12, 12, 0.30, 13.5, 0.52, 15, 0.72, 17, 0.86]);
  safePaint(map, "buildings", "fill-color", "#a89a86");
  safePaint(map, "buildings", "fill-outline-color", "#756b5f");
  safePaint(map, "buildings", "fill-opacity", topographic ? 0.52 : 0.60);

  // Etiquetas integradas con el terreno; la navegación activa conserva sus colores propios.
  safePaint(map, "road-main-labels", "text-color", "#514d46");
  safePaint(map, "road-main-labels", "text-halo-color", "rgba(244, 240, 226, 0.92)");
  safePaint(map, "road-local-labels", "text-color", "#625e56");
  safePaint(map, "road-local-labels", "text-halo-color", "rgba(244, 240, 226, 0.88)");
  safePaint(map, "mountain-labels", "text-color", "#453d36");
  safePaint(map, "mountain-labels", "text-halo-color", "rgba(246, 242, 229, 0.94)");
}

function applyRegionalContourProfile(map: maplibregl.Map, mode: LayerMode) {
  const topographic = mode === "relief";
  const outdoor = mode === "3d";
  const visible = topographic || outdoor;
  ["thera-contours-minor", "thera-contours-intermediate", "thera-contours-index", "thera-contours-labels"]
    .forEach((id) => safeLayout(map, id, "visibility", visible ? "visible" : "none"));
  if (!visible) return;

  safePaint(map, "thera-contours-minor", "line-color", topographic ? "#78644f" : "#70675a");
  safePaint(map, "thera-contours-minor", "line-opacity", ["interpolate", ["linear"], ["zoom"], 13, topographic ? 0.24 : 0.06, 14, topographic ? 0.34 : 0.12, 16, topographic ? 0.46 : 0.22]);
  safePaint(map, "thera-contours-minor", "line-width", ["interpolate", ["linear"], ["zoom"], 13, topographic ? 0.34 : 0.24, 16, topographic ? 0.62 : 0.46]);
  safePaint(map, "thera-contours-intermediate", "line-color", topographic ? "#695640" : "#665e52");
  safePaint(map, "thera-contours-intermediate", "line-opacity", ["interpolate", ["linear"], ["zoom"], 12, topographic ? 0.34 : 0.10, 13.5, topographic ? 0.46 : 0.20, 16, topographic ? 0.60 : 0.34]);
  safePaint(map, "thera-contours-intermediate", "line-width", ["interpolate", ["linear"], ["zoom"], 12, topographic ? 0.48 : 0.34, 16, topographic ? 0.84 : 0.64]);
  safePaint(map, "thera-contours-index", "line-color", topographic ? "#514332" : "#554f46");
  safePaint(map, "thera-contours-index", "line-opacity", ["interpolate", ["linear"], ["zoom"], 10.5, topographic ? 0.54 : 0.24, 12, topographic ? 0.68 : 0.34, 16, topographic ? 0.82 : 0.50]);
  safePaint(map, "thera-contours-index", "line-width", ["interpolate", ["linear"], ["zoom"], 10.5, topographic ? 0.70 : 0.54, 16, topographic ? 1.18 : 0.90]);
  safePaint(map, "thera-contours-labels", "text-color", topographic ? "#554535" : "#59544b");
  safePaint(map, "thera-contours-labels", "text-opacity", ["interpolate", ["linear"], ["zoom"], 12.5, topographic ? 0.72 : 0.38, 14, topographic ? 0.90 : 0.66]);
  safePaint(map, "thera-contours-labels", "text-halo-color", topographic ? "rgba(248, 243, 229, 0.95)" : "rgba(239, 237, 224, 0.82)");
  safePaint(map, "thera-contours-labels", "text-halo-width", topographic ? 1.25 : 0.9);
}


function restoreNationalVectorProfile(map: maplibregl.Map) {
  // Restaura exactamente el perfil base aprobado de Ecuador esencial al salir
  // de Outdoor 3D. Solo se usa en web nacional.
  safePaint(map, "landcover", "fill-opacity", 0.70);
  safePaint(map, "landuse", "fill-opacity", 0.38);
  safePaint(map, "water", "fill-color", "#69b7d0");
  safePaint(map, "water", "fill-opacity", 0.88);
  safePaint(map, "water-lines", "line-color", "#429bb8");
  safePaint(map, "water-lines", "line-opacity", 1);
  safePaint(map, "road-casing", "line-color", "#777d76");
  safePaint(map, "road-casing", "line-opacity", 0.8);
  safePaint(map, "roads", "line-color", "#f3ead7");
  safePaint(map, "roads", "line-opacity", 1);
  safePaint(map, "trails", "line-color", "#9a5f2f");
  safePaint(map, "trails", "line-opacity", 1);
  safePaint(map, "buildings", "fill-color", "#b9aa98");
  safePaint(map, "buildings", "fill-outline-color", "#81786d");
  safePaint(map, "buildings", "fill-opacity", 0.72);
  safePaint(map, "road-main-labels", "text-color", "#4c5557");
  safePaint(map, "road-main-labels", "text-halo-color", "rgba(255, 253, 245, 0.96)");
  safePaint(map, "road-local-labels", "text-color", "#6b6f6c");
  safePaint(map, "road-local-labels", "text-halo-color", "rgba(255, 253, 245, 0.94)");
  safePaint(map, "mountain-labels", "text-color", "#473d35");
  safePaint(map, "mountain-labels", "text-halo-color", "rgba(255, 253, 245, 0.96)");
}

function applyNationalWebContourProfile(map: maplibregl.Map, outdoor: boolean) {
  if (outdoor) {
    // Reutiliza exactamente el contraste Outdoor regional ya validado.
    applyRegionalContourProfile(map, "3d");
    return;
  }
  // Perfil topografico nacional aprobado.
  safePaint(map, "thera-contours-minor", "line-color", "#7d674f");
  safePaint(map, "thera-contours-minor", "line-opacity", 0.48);
  safePaint(map, "thera-contours-minor", "line-width", ["interpolate", ["linear"], ["zoom"], 12, 0.38, 16, 0.72]);
  safePaint(map, "thera-contours-intermediate", "line-color", "#70583f");
  safePaint(map, "thera-contours-intermediate", "line-opacity", 0.62);
  safePaint(map, "thera-contours-intermediate", "line-width", ["interpolate", ["linear"], ["zoom"], 11.5, 0.52, 16, 0.95]);
  safePaint(map, "thera-contours-index", "line-color", "#59442f");
  safePaint(map, "thera-contours-index", "line-opacity", 0.82);
  safePaint(map, "thera-contours-index", "line-width", ["interpolate", ["linear"], ["zoom"], 10, 0.82, 16, 1.28]);
  safePaint(map, "thera-contours-labels", "text-color", "#594532");
  safePaint(map, "thera-contours-labels", "text-opacity", 1);
  safePaint(map, "thera-contours-labels", "text-halo-color", "rgba(248, 243, 229, 0.95)");
  safePaint(map, "thera-contours-labels", "text-halo-width", 1.35);
}

function remoteMapUrl(file: string) {
  const base = import.meta.env.VITE_MAPS_API_BASE?.replace(/\/$/, "") ?? "";
  return new URL(`${base}/api/maps/${file}`, window.location.origin).href;
}

// Curvas: en web/PWA usamos same-origin. En Android/Capacitor el WebView
// puede ejecutarse desde localhost/capacitor://, por lo que /api/maps/...
// apuntaría al shell local y no a Cloudflare. En ese caso usamos la base API
// configurada y, como respaldo, el dominio de producción de THERA.

// THERA — curvas regionales offline + curvas nacionales solo web.
// Ecuador esencial usa ecuador-esencial-curvas-v1.pmtiles mediante un protocolo
// web dedicado que descomprime MVT gzip; Android conserva los paquetes regionales locales.



function registerProtocol() {
  if (protocolRegistered) return;
  maplibregl.addProtocol("thera", async (request, controller) => {
    const match = /^thera:\/\/([^/]+)\/(\d+)\/(\d+)\/(\d+)$/.exec(request.url);
    if (!match) throw new Error("Ruta de tesela THERA inválida.");
    const archive = archives.get(match[1]);
    if (!archive) throw new Error("El paquete regional ya no está disponible.");
    const tile = await archive.getZxy(Number(match[2]), Number(match[3]), Number(match[4]), controller.signal);
    controller.signal.throwIfAborted();
    return { data: tile ? new Uint8Array(tile.data) : new Uint8Array() };
  });
  protocolRegistered = true;
}

// Protocolo exclusivo para las curvas nacionales WEB.
// PMTiles almacena las teselas MVT comprimidas con gzip; aquí leemos cada tesela
// por Range request y la descomprimimos antes de entregarla a MapLibre.
// Android no usa este protocolo: continúa con sus PMTiles regionales locales.
async function ungzipIfNeeded(data: ArrayBuffer) {
  const bytes = new Uint8Array(data);
  if (bytes.length < 2 || bytes[0] !== 0x1f || bytes[1] !== 0x8b) return bytes;
  if (typeof DecompressionStream === "undefined") return bytes;
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function registerContourWebProtocol() {
  if (contourWebProtocolRegistered) return;
  maplibregl.addProtocol("theracontour", async (request, controller) => {
    const match = /^theracontour:\/\/([^/]+)\/(\d+)\/(\d+)\/(\d+)$/.exec(request.url);
    if (!match) throw new Error("Ruta de curva THERA inválida.");
    const archive = contourWebArchives.get(match[1]);
    if (!archive) throw new Error("Las curvas nacionales ya no están disponibles.");
    try {
      const tile = await archive.getZxy(Number(match[2]), Number(match[3]), Number(match[4]), controller.signal);
      if (controller.signal.aborted || !tile) return { data: new Uint8Array() };
      return { data: await ungzipIfNeeded(tile.data) };
    } catch (error) {
      if (controller.signal.aborted) return { data: new Uint8Array() };
      throw error;
    }
  });
  contourWebProtocolRegistered = true;
}

// THERA Natural 3D — Etapa 2 + Topografico 3D — Etapa 3.
// Reutiliza PMTiles y DEM existentes; no altera GPS, rutas, offline ni navegacion.
function theraStyle(archiveKey: string, header: Header, regional3D = false): StyleSpecification {
  return {
    version: 8,
    sources: {
      thera: {
        type: "vector",
        tiles: [`thera://${archiveKey}/{z}/{x}/{y}`],
        minzoom: header.minZoom,
        maxzoom: header.maxZoom,
        bounds: [header.minLon, header.minLat, header.maxLon, header.maxLat],
        attribution: "© OpenStreetMap contributors",
      },
    },
    layers: [
      { id: "background", type: "background", paint: { "background-color": "#dfe7d8" } },
      { id: "earth", type: "fill", source: "thera", "source-layer": "earth", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": regional3D ? "#d5cfb8" : "#d9d7c2" } },
      { id: "landcover", type: "fill", source: "thera", "source-layer": "landcover", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": regional3D ? "#829f75" : "#8fb78a", "fill-opacity": regional3D ? 0.58 : 0.70 } },
      { id: "landuse", type: "fill", source: "thera", "source-layer": "landuse", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": regional3D ? "#b2b493" : "#b7c49a", "fill-opacity": regional3D ? 0.28 : 0.38 } },
      {
        id: "water",
        type: "fill",
        source: "thera",
        "source-layer": "water",
        // Protomaps reúne polígonos, ejes de ríos y puntos de etiquetas en esta
        // misma capa. Rellenar líneas o puntos crea grandes triángulos azules.
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: { "fill-color": regional3D ? "#5ca8c1" : "#69b7d0", "fill-opacity": regional3D ? 0.90 : 0.88 },
      },
      {
        id: "water-lines",
        type: "line",
        source: "thera",
        "source-layer": "water",
        filter: ["==", ["geometry-type"], "LineString"],
        paint: { "line-color": regional3D ? "#3f93ad" : "#429bb8", "line-width": ["interpolate", ["linear"], ["zoom"], 7, 0.45, 15, 2], "line-opacity": regional3D ? 0.80 : 1 },
        layout: { "line-cap": "round", "line-join": "round" },
      },
      { id: "boundaries", type: "line", source: "thera", "source-layer": "boundaries", paint: { "line-color": "#72808a", "line-width": 1, "line-dasharray": [3, 2], "line-opacity": 0.75 } },
      {
        id: "road-casing", type: "line", source: "thera", "source-layer": "roads",
        ...(regional3D ? { filter: ["!=", ["get", "kind"], "path"] } : {}),
        paint: { "line-color": regional3D ? "#6f675b" : "#777d76", "line-width": ["interpolate", ["linear"], ["zoom"], 7, 1.0, 15, regional3D ? 4.5 : 5.2], "line-opacity": regional3D ? 0.44 : 0.8 },
        layout: { "line-cap": "round", "line-join": "round" }
      },
      {
        id: "roads", type: "line", source: "thera", "source-layer": "roads",
        ...(regional3D ? { filter: ["!=", ["get", "kind"], "path"] } : {}),
        paint: {
          "line-color": regional3D ? ["match", ["get", "kind"], "highway", "#d8d0bc", "major_road", "#d1c7b2", "medium_road", "#c7bba4", "minor_road", "#b9ae9a", "#ad9d86"] : "#f3ead7",
          "line-width": ["interpolate", ["linear"], ["zoom"], 7, regional3D ? 0.55 : 0.7, 15, regional3D ? 2.8 : 3.3],
          "line-opacity": regional3D ? ["interpolate", ["linear"], ["zoom"], 7, 0.38, 10, 0.58, 13, 0.78, 16, 0.90] : 1
        },
        layout: { "line-cap": "round", "line-join": "round" }
      },
      {
        id: "trails", type: "line", source: "thera", "source-layer": "roads", minzoom: regional3D ? 10.5 : 0,
        filter: ["==", ["get", "kind"], "path"],
        paint: { "line-color": regional3D ? "#88735d" : "#9a5f2f", "line-width": ["interpolate", ["linear"], ["zoom"], 10, regional3D ? 0.75 : 1, 15, regional3D ? 1.6 : 2.2], "line-dasharray": [2, 1.5], "line-opacity": regional3D ? ["interpolate", ["linear"], ["zoom"], 10, 0.38, 12, 0.58, 15, 0.82, 17, 0.92] : 1 },
        layout: { "line-cap": "round", "line-join": "round" }
      },
      { id: "buildings", type: "fill", source: "thera", "source-layer": "buildings", minzoom: 12, filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": regional3D ? "#a89a86" : "#b9aa98", "fill-outline-color": regional3D ? "#756b5f" : "#81786d", "fill-opacity": regional3D ? 0.60 : 0.72 } },
      { id: "pois", type: "circle", source: "thera", "source-layer": "pois", minzoom: 12, filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 12, 1.5, 16, 3], "circle-color": "#1b3148", "circle-stroke-color": "#ffffff", "circle-stroke-width": 1, "circle-opacity": 0.78 } },
      {
        id: "road-main-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "roads",
        minzoom: 10.5,
        filter: ["all", ["has", "name"], ["in", ["get", "kind"], ["literal", ["highway", "major_road", "medium_road"]]]],
        layout: {
          "symbol-placement": "line",
          "symbol-spacing": 480,
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 10.5, 10, 15, 12.5],
          "text-letter-spacing": 0.02,
          "text-max-angle": 35,
          "text-rotation-alignment": "map",
        },
        paint: {
          "text-color": "#4c5557",
          "text-halo-color": "rgba(255, 253, 245, 0.96)",
          "text-halo-width": 1.6,
          "text-halo-blur": 0.3,
        },
      },
      {
        id: "road-local-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "roads",
        minzoom: 13.5,
        filter: ["all", ["has", "name"], ["==", ["get", "kind"], "minor_road"]],
        layout: {
          "symbol-placement": "line",
          "symbol-spacing": 620,
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 13.5, 9, 16, 11],
          "text-max-angle": 35,
          "text-rotation-alignment": "map",
        },
        paint: {
          "text-color": "#6b6f6c",
          "text-halo-color": "rgba(255, 253, 245, 0.94)",
          "text-halo-width": 1.4,
        },
      },
      {
        id: "water-area-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "water",
        minzoom: 10.5,
        filter: ["all", ["has", "name"], ["==", ["geometry-type"], "Polygon"]],
        layout: {
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 10.5, 10, 15, 13],
          "text-max-width": 8,
          "text-letter-spacing": 0.03,
        },
        paint: {
          "text-color": "#23778e",
          "text-halo-color": "rgba(239, 248, 249, 0.94)",
          "text-halo-width": 1.5,
          "text-halo-blur": 0.25,
        },
      },
      {
        id: "water-point-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "water",
        minzoom: 15,
        filter: [
          "all",
          ["has", "name"],
          ["==", ["geometry-type"], "Point"],
          ["!", ["in", ["get", "name"], ["literal", ["S/N", "SN", "Sin nombre"]]]],
        ],
        layout: {
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 15, 10, 17, 13],
          "text-max-width": 9,
          "text-letter-spacing": 0.025,
          "text-padding": 5,
          "symbol-sort-key": ["coalesce", ["get", "min_zoom"], 16],
        },
        paint: {
          "text-color": "#176f88",
          "text-halo-color": "rgba(239, 248, 249, 0.97)",
          "text-halo-width": 1.8,
          "text-halo-blur": 0.25,
        },
      },
      {
        id: "water-line-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "water",
        minzoom: 11.5,
        filter: ["all", ["has", "name"], ["==", ["geometry-type"], "LineString"]],
        layout: {
          "symbol-placement": "line",
          "symbol-spacing": 420,
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 11.5, 9.5, 16, 12],
          "text-max-angle": 35,
          "text-rotation-alignment": "map",
        },
        paint: {
          "text-color": "#297e94",
          "text-halo-color": "rgba(239, 248, 249, 0.96)",
          "text-halo-width": 1.4,
        },
      },
      {
        id: "place-city-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "places",
        minzoom: 8,
        filter: ["all", ["has", "name"], ["in", ["get", "kind"], ["literal", ["city", "town", "locality"]]]],
        layout: {
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 8, 12, 15, 17],
          "text-letter-spacing": 0.025,
          "text-max-width": 9,
        },
        paint: {
          "text-color": "#172e45",
          "text-halo-color": "rgba(255, 255, 255, 0.96)",
          "text-halo-width": 2,
          "text-halo-blur": 0.25,
        },
      },
      {
        id: "place-local-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "places",
        minzoom: 11,
        filter: ["all", ["has", "name"], ["in", ["get", "kind"], ["literal", ["village", "hamlet", "suburb", "neighbourhood"]]]],
        layout: {
          "text-field": ["coalesce", ["get", "name:es"], ["get", "name"]],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 11, 10, 16, 13],
          "text-letter-spacing": 0.015,
          "text-max-width": 8,
        },
        paint: {
          "text-color": "#33485a",
          "text-halo-color": "rgba(255, 255, 255, 0.95)",
          "text-halo-width": 1.7,
        },
      },
      {
        id: "mountain-labels",
        type: "symbol",
        source: "thera",
        "source-layer": "pois",
        minzoom: regional3D ? 12.3 : 13,
        filter: ["all", ["has", "name"], ["==", ["get", "kind"], "peak"]],
        layout: {
          "text-field": [
            "case",
            ["has", "elevation"],
            ["concat", "▲ ", ["coalesce", ["get", "name:es"], ["get", "name"]], "\n", ["to-string", ["get", "elevation"]], " m"],
            ["concat", "▲ ", ["coalesce", ["get", "name:es"], ["get", "name"]]],
          ],
          "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 13, 10, 16, 13],
          "text-line-height": 1.1,
          "text-letter-spacing": 0.015,
          "text-offset": [0, 0.8],
          "text-anchor": "top",
          "text-max-width": 9,
        },
        paint: {
          "text-color": "#473d35",
          "text-halo-color": "rgba(255, 253, 245, 0.96)",
          "text-halo-width": 1.8,
          "text-halo-blur": 0.25,
        },
      },
    ],
  };
}

async function readHeader(archive: PMTiles) {
  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      archive.getHeader(),
      new Promise<never>((_resolve, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error("MAP_TIMEOUT")), 15000);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

type TheraMapLibreProps = {
  file: string;
  contourFile?: string;
  center: [number, number];
  zoom?: number;
  routeGeojson?: RouteGeoJson;
  recordedTrack?: TrackPoint[];
  currentPosition?: TrackPoint;
  currentHeadingDegrees?: number | null;
  recenterSignal?: number;
  recentreSignal?: number;
  approachTarget?: [number, number];
  roadApproachCoordinates?: number[][];
  followUser?: boolean;
  searchTarget?: { longitude: number; latitude: number; label: string };
  allowGuestViewer?: boolean;
  persistDownloadedMaps?: boolean;
  onSnapshotProvider?: (provider: (() => string | null) | null) => void;
  plannerPoints?: Array<{ id: string; kind: "start" | "waypoint" | "finish"; latitude: number; longitude: number }>;
  plannerRouteCoordinates?: number[][];
  plannerRouteHasFallback?: boolean;
  plannerActive?: boolean;
  onPlannerMapClick?: (coordinate: { latitude: number; longitude: number }) => void;
  personalMapGeojson?: maplibregl.GeoJSONSourceSpecification["data"];
  personalMapFocusBounds?: {
    west: number;
    south: number;
    east: number;
    north: number;
  };
  onPersonalMapTrackSelect?: (activityId: string | null) => void;
};

function lineFeature(geojson?: RouteGeoJson) {
  return geojson?.features.find((feature) => feature.geometry.type === "LineString");
}

function emptyFeatureCollection() {
  return { type: "FeatureCollection" as const, features: [] };
}

function recordedTrackFeature(points: TrackPoint[]) {
  return points.length > 1 ? {
    type: "Feature" as const,
    properties: {},
    geometry: {
      type: "LineString" as const,
      coordinates: points.map((point) => point.altitude === null
        ? [point.longitude, point.latitude]
        : [point.longitude, point.latitude, point.altitude]),
    },
  } : emptyFeatureCollection();
}

function currentPositionFeature(point?: TrackPoint, headingDegrees?: number | null) {
  return point ? {
    type: "Feature" as const,
    properties: { accuracy: point.accuracy, heading: Number.isFinite(headingDegrees) ? headingDegrees : 0 },
    geometry: { type: "Point" as const, coordinates: [point.longitude, point.latitude] },
  } : emptyFeatureCollection();
}

function plannerPointsFeature(points: NonNullable<TheraMapLibreProps["plannerPoints"]>) {
  return {
    type: "FeatureCollection" as const,
    features: points.map((point, index) => ({
      type: "Feature" as const,
      properties: {
        id: point.id,
        kind: point.kind,
        name: point.kind === "start" ? "Inicio" : point.kind === "finish" ? "Destino" : `Punto ${index}`,
      },
      geometry: { type: "Point" as const, coordinates: [point.longitude, point.latitude] },
    })),
  };
}

function plannerRouteFeature(coordinates: number[][]) {
  return coordinates.length > 1 ? {
    type: "Feature" as const,
    properties: {},
    geometry: { type: "LineString" as const, coordinates },
  } : emptyFeatureCollection();
}

function searchTargetFeature(target?: TheraMapLibreProps["searchTarget"]) {
  return target ? {
    type: "Feature" as const,
    properties: { name: target.label },
    geometry: { type: "Point" as const, coordinates: [target.longitude, target.latitude] },
  } : emptyFeatureCollection();
}

function approachLineFeature(point?: TrackPoint, target?: [number, number]) {
  return point && target ? {
    type: "Feature" as const,
    properties: { kind: "route-approach" },
    geometry: {
      type: "LineString" as const,
      coordinates: [[point.longitude, point.latitude], target],
    },
  } : emptyFeatureCollection();
}

function roadApproachFeature(coordinates?: number[][]) {
  return coordinates && coordinates.length > 1 ? {
    type: "Feature" as const,
    properties: { kind: "road-approach" },
    geometry: { type: "LineString" as const, coordinates },
  } : emptyFeatureCollection();
}

export function TheraMapLibre({
  file,
  contourFile,
  center,
  zoom = 11,
  routeGeojson,
  recordedTrack = [],
  currentPosition,
  currentHeadingDegrees,
  recenterSignal,
  recentreSignal,
  approachTarget,
  roadApproachCoordinates,
  followUser = false,
  searchTarget,
  allowGuestViewer = false,
  persistDownloadedMaps = true,
  onSnapshotProvider,
  plannerPoints = [],
  plannerRouteCoordinates = [],
  plannerRouteHasFallback = false,
  plannerActive = false,
  onPlannerMapClick,
  personalMapGeojson,
  personalMapFocusBounds,
  onPersonalMapTrackSelect,
}: TheraMapLibreProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const terrainArchiveKey = useRef<string | undefined>(undefined);
  const contourArchiveKey = useRef<string | undefined>(undefined);
  const derivedOverlayArchiveKey = useRef<string | undefined>(undefined);
  const activeFile = useRef(file);
  const [message, setMessage] = useState("Preparando mapa seguro…");
  const [layerMode, setLayerMode] = useState<LayerMode>("map");
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const [terrainLoading, setTerrainLoading] = useState(false);
  const [derivedOverlayAdmin, setDerivedOverlayAdmin] = useState(false);
  const [derivedOverlayEnabled, setDerivedOverlayEnabled] = useState(false);
  const [derivedOverlayBusy, setDerivedOverlayBusy] = useState(false);
  const [derivedOverlayReleaseLabel, setDerivedOverlayReleaseLabel] = useState<string | null>(null);
  const [derivedOverlayOfflineStored, setDerivedOverlayOfflineStored] = useState(false);
  const [derivedOverlayDownloadProgress, setDerivedOverlayDownloadProgress] = useState<number | null>(null);
  const derivedOfflineRuntime = derivedLayerOfflineRuntimeEnabled();

  activeFile.current = file;

  useEffect(() => {
    let cancelled = false;
    if (!THERA_DERIVED_LAYER_RENDERING_ENABLED) {
      setDerivedOverlayAdmin(false);
      setDerivedOverlayOfflineStored(false);
      return () => { cancelled = true; };
    }

    void (async () => {
      if (derivedOfflineRuntime) {
        const stored = await getStoredDerivedLayerForCurrentSession("sierra-sur", "field").catch(() => null);
        if (cancelled) return;
        setDerivedOverlayOfflineStored(Boolean(stored));
        if (stored) {
          setDerivedOverlayAdmin(true);
          setDerivedOverlayReleaseLabel(`V${stored.version} · ${stored.region_id} · offline`);
          return;
        }
      }

      const allowed = await currentUserCanRenderDerivedLayer().catch(() => false);
      if (!cancelled) setDerivedOverlayAdmin(allowed);
    })();

    return () => { cancelled = true; };
  }, [derivedOfflineRuntime]);

  const removeDerivedFieldOverlay = (activeMap = mapRef.current) => {
    if (!activeMap) return;
    ["thera-derived-field-line", "thera-derived-field-casing"].forEach((layerId) => {
      if (activeMap.getLayer(layerId)) activeMap.removeLayer(layerId);
    });
    if (activeMap.getSource("thera-derived-field")) activeMap.removeSource("thera-derived-field");
    if (derivedOverlayArchiveKey.current) {
      archives.delete(derivedOverlayArchiveKey.current);
      derivedOverlayArchiveKey.current = undefined;
    }
    setDerivedOverlayEnabled(false);
    setDerivedOverlayReleaseLabel(null);
  };

  const attachDerivedFieldArchive = async (
    activeMap: maplibregl.Map,
    archive: PMTiles,
    label: string,
    message: string,
  ) => {
    const header = await readHeader(archive);
    if (mapRef.current !== activeMap) return;

    const key = `derived-field-${++archiveSequence}`;
    archives.set(key, archive);
    derivedOverlayArchiveKey.current = key;

    activeMap.addSource("thera-derived-field", {
      type: "vector",
      tiles: [`thera://${key}/{z}/{x}/{y}`],
      minzoom: header.minZoom,
      maxzoom: header.maxZoom,
      bounds: [header.minLon, header.minLat, header.maxLon, header.maxLat],
      attribution: "THERA Field Survey · capa derivada",
    });

    const beforeLayer = activeMap.getLayer("thera-route-shadow")
      ? "thera-route-shadow"
      : activeMap.getLayer("thera-recorded-track-shadow")
        ? "thera-recorded-track-shadow"
        : undefined;

    activeMap.addLayer({
      id: "thera-derived-field-casing",
      type: "line",
      source: "thera-derived-field",
      "source-layer": "field",
      paint: {
        "line-color": "#ffffff",
        "line-width": ["interpolate", ["linear"], ["zoom"], 8, 2.5, 12, 4, 16, 7],
        "line-opacity": 0.92,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    }, beforeLayer);
    activeMap.addLayer({
      id: "thera-derived-field-line",
      type: "line",
      source: "thera-derived-field",
      "source-layer": "field",
      paint: {
        "line-color": "#ff7f00",
        "line-width": ["interpolate", ["linear"], ["zoom"], 8, 1.4, 12, 2.4, 16, 4.2],
        "line-opacity": 0.92,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    }, beforeLayer);

    setDerivedOverlayEnabled(true);
    setDerivedOverlayReleaseLabel(label);
    setMessage(message);
    window.setTimeout(() => setMessage(""), 2600);
  };

  const archiveFromStoredDerived = async (stored: StoredDerivedLayerRelease) => {
    const bytes = await stored.blob.arrayBuffer();
    const localFile = new File([bytes], stored.alias, { type: "application/vnd.pmtiles" });
    return new PMTiles(new FileSource(localFile));
  };

  const toggleDerivedFieldOverlay = async () => {
    const activeMap = mapRef.current;
    if (!activeMap || derivedOverlayBusy || !derivedOverlayAdmin) return;

    if (derivedOverlayEnabled) {
      removeDerivedFieldOverlay(activeMap);
      setMessage("");
      return;
    }

    setDerivedOverlayBusy(true);
    setDerivedOverlayDownloadProgress(null);
    setLayerMenuOpen(false);
    try {
      if (derivedOfflineRuntime) {
        let stored = await getStoredDerivedLayerForCurrentSession("sierra-sur", "field");
        if (!stored) {
          if (!navigator.onLine) {
            throw new Error("Conéctate una vez para descargar Field Survey V1.");
          }
          const allowed = await currentUserCanRenderDerivedLayer().catch(() => false);
          if (!allowed) throw new Error("La descarga offline PHASE3K requiere administrador aprobado.");
          const release = await getActiveDerivedLayerRelease("sierra-sur", "field");
          if (!release) throw new Error("No existe release activo sierra-sur · field.");
          stored = await downloadDerivedLayerForOffline(release, (progress) => {
            setDerivedOverlayDownloadProgress(progress);
          });
          setDerivedOverlayOfflineStored(true);
        }

        const archive = await archiveFromStoredDerived(stored);
        await attachDerivedFieldArchive(
          activeMap,
          archive,
          `V${stored.version} · ${stored.region_id} · offline`,
          "Field Survey V1 activo desde almacenamiento offline.",
        );
        return;
      }

      const release = await getActiveDerivedLayerRelease("sierra-sur", "field");
      if (!release) throw new Error("No existe release activo sierra-sur · field.");

      const probe = await probeActiveDerivedLayerAvailability(release);
      if (!probe.size_matches) {
        throw new Error("El tamaño del PMTiles derivado no coincide con el release activo.");
      }

      const access = await getDerivedLayerMapAccess(release);
      const archive = new PMTiles(new FetchSource(access.url, access.headers));
      await attachDerivedFieldArchive(
        activeMap,
        archive,
        `V${release.version} · ${release.region_id}`,
        "Overlay Field Survey V1 activo · Preview administrador.",
      );
    } catch (error) {
      removeDerivedFieldOverlay(activeMap);
      const detail = error instanceof Error ? error.message : "No fue posible abrir la capa derivada.";
      setMessage(detail);
    } finally {
      setDerivedOverlayBusy(false);
      setDerivedOverlayDownloadProgress(null);
    }
  };

  const setContourVisibility = (visible: boolean) => {
    const activeMap = mapRef.current;
    if (!activeMap) return;
    [
      "thera-contours-minor",
      "thera-contours-intermediate",
      "thera-contours-index",
      "thera-contours-labels",
    ].forEach((layerId) => {
      if (activeMap.getLayer(layerId)) {
        activeMap.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
      }
    });
  };

  const ensureContourLayers = async (activeMap: maplibregl.Map) => {
    // Ecuador esencial: curvas nacionales SOLO en navegador web real.
    // Android/Capacitor y los paquetes regionales siguen usando exactamente
    // el flujo local validado con FileSource + `thera://`.
    const isNativeShell =
      (window as any).Capacitor?.isNativePlatform?.() === true ||
      window.location.protocol === "capacitor:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const nationalWebContours =
      file === "ecuador-esencial.pmtiles" &&
      !isNativeShell;

    const resolvedContourFile = nationalWebContours
      ? "ecuador-esencial-curvas-v1.pmtiles"
      : (contourFile ?? contourFileFor(file));
    if (!resolvedContourFile) return false;

    if (activeMap.getSource("thera-contours")) {
      setContourVisibility(true);
      return true;
    }

    const stored = nationalWebContours
      ? undefined
      : await getStoredMapPack(resolvedContourFile).catch(() => undefined);

    if (!nationalWebContours && !stored?.blob) {
      setMessage("");
      return false;
    }

    try {
      setMessage("Preparando curvas topográficas…");

      if (nationalWebContours) {
        // Ecuador esencial WEB: controlamos la lectura PMTiles directamente.
        // Esto evita que MapLibre reciba PBF gzip sin cabecera Content-Encoding y
        // elimina los falsos "Failed to fetch" de solicitudes abortadas al mover el mapa.
        registerContourWebProtocol();
        const contourArchive = new PMTiles(remoteMapUrl(resolvedContourFile));
        const contourHeader = await readHeader(contourArchive);
        if (activeFile.current !== file || mapRef.current !== activeMap) return false;
        const key = `web-contours-${++archiveSequence}`;
        contourWebArchives.set(key, contourArchive);
        contourArchiveKey.current = key;
        activeMap.addSource("thera-contours", {
          type: "vector",
          tiles: [`theracontour://${key}/{z}/{x}/{y}`],
          minzoom: contourHeader.minZoom,
          maxzoom: contourHeader.maxZoom,
          bounds: [contourHeader.minLon, contourHeader.minLat, contourHeader.maxLon, contourHeader.maxLat],
          attribution: "THERA · curvas topográficas",
        });
      } else {
        const bytes = await Promise.race([
          stored!.blob.arrayBuffer(),
          new Promise<ArrayBuffer>((_resolve, reject) =>
            window.setTimeout(() => reject(new Error("CONTOUR_BLOB_TIMEOUT")), 12000),
          ),
        ]);
        const localFile = new File([bytes], resolvedContourFile, { type: "application/vnd.pmtiles" });
        const contourArchive = new PMTiles(new FileSource(localFile));
        const contourHeader = await readHeader(contourArchive);
        if (activeFile.current !== file || mapRef.current !== activeMap) return false;

        const key = `contours-${++archiveSequence}`;
        archives.set(key, contourArchive);
        contourArchiveKey.current = key;
        activeMap.addSource("thera-contours", {
          type: "vector",
          tiles: [`thera://${key}/{z}/{x}/{y}`],
          minzoom: contourHeader.minZoom,
          maxzoom: contourHeader.maxZoom,
          bounds: [contourHeader.minLon, contourHeader.minLat, contourHeader.maxLon, contourHeader.maxLat],
        });
      }

      if (activeFile.current !== file || mapRef.current !== activeMap) return false;

      // Render de curvas:
      // - Ecuador esencial WEB usa el protocolo PMTiles nativo (Range requests).
      // - Android/regionales conservan exactamente el flujo local aprobado.
      if (nationalWebContours) {
        const beforeRoads = activeMap.getLayer("road-casing") ? "road-casing" : undefined;
        const beforeLabels = activeMap.getLayer("road-main-labels") ? "road-main-labels" : undefined;

        // Capa base: dibuja todas las líneas del source-layer "contours".
        // Así no dependemos de que class/interval estén presentes en cada feature.
        activeMap.addLayer({
          id: "thera-contours-minor",
          type: "line",
          source: "thera-contours",
          "source-layer": "contours",
          minzoom: 10,
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#7d674f",
            "line-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.28, 12, 0.40, 14, 0.52, 16, 0.62],
            "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.34, 12, 0.46, 14, 0.62, 16, 0.80],
          },
        }, beforeRoads);

        // Curvas intermedias. Preferimos interval=50 cuando está disponible;
        // como respaldo, usamos ele múltiplo de 50.
        activeMap.addLayer({
          id: "thera-contours-intermediate",
          type: "line",
          source: "thera-contours",
          "source-layer": "contours",
          minzoom: 10.5,
          filter: [
            "any",
            ["==", ["get", "interval"], 50],
            ["==", ["get", "class"], "intermediate"],
            ["all",
              ["has", "ele"],
              ["==", ["%", ["to-number", ["get", "ele"]], 50], 0]
            ],
          ],
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#6e583f",
            "line-opacity": ["interpolate", ["linear"], ["zoom"], 10.5, 0.40, 12, 0.54, 14, 0.68, 16, 0.76],
            "line-width": ["interpolate", ["linear"], ["zoom"], 10.5, 0.48, 12, 0.64, 14, 0.88, 16, 1.08],
          },
        }, beforeRoads);

        // Curvas índice de 100 m.
        activeMap.addLayer({
          id: "thera-contours-index",
          type: "line",
          source: "thera-contours",
          "source-layer": "contours",
          minzoom: 10,
          filter: [
            "any",
            ["==", ["get", "interval"], 100],
            ["==", ["get", "class"], "index"],
            ["all",
              ["has", "ele"],
              ["==", ["%", ["to-number", ["get", "ele"]], 100], 0]
            ],
          ],
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#4f3f2f",
            "line-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.58, 12, 0.72, 14, 0.86, 16, 0.92],
            "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.66, 12, 0.88, 14, 1.14, 16, 1.40],
          },
        }, beforeRoads);

        // Cotas de las curvas índice.
        activeMap.addLayer({
          id: "thera-contours-labels",
          type: "symbol",
          source: "thera-contours",
          "source-layer": "contours",
          minzoom: 11,
          filter: [
            "all",
            ["has", "ele"],
            ["==", ["%", ["to-number", ["get", "ele"]], 100], 0],
          ],
          layout: {
            visibility: "visible",
            "symbol-placement": "line",
            "symbol-spacing": 380,
            "text-field": ["concat", ["to-string", ["round", ["to-number", ["get", "ele"]]]], " m"],
            "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
            "text-size": ["interpolate", ["linear"], ["zoom"], 11, 8.5, 13, 9.5, 16, 11],
            "text-rotation-alignment": "map",
            "text-max-angle": 30,
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#4f3f2f",
            "text-halo-color": "rgba(248, 243, 229, 0.96)",
            "text-halo-width": 1.4,
          },
        }, beforeLabels);
      } else {
        // Flujo regional/Android original: no se modifica.
        const beforeLayer = activeMap.getLayer("water") ? "water" : undefined;
        activeMap.addLayer({
          id: "thera-contours-minor", type: "line", source: "thera-contours", "source-layer": "contours", minzoom: 12,
          filter: ["any", ["==", ["get", "class"], "minor"], ["==", ["get", "interval"], 20]],
          layout: { visibility: "visible", "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#7d674f", "line-opacity": 0.48, "line-width": ["interpolate", ["linear"], ["zoom"], 12, 0.38, 16, 0.72] },
        }, beforeLayer);
        activeMap.addLayer({
          id: "thera-contours-intermediate", type: "line", source: "thera-contours", "source-layer": "contours", minzoom: 11.5,
          filter: ["any", ["==", ["get", "class"], "intermediate"], ["==", ["get", "interval"], 50]],
          layout: { visibility: "visible", "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#70583f", "line-opacity": 0.62, "line-width": ["interpolate", ["linear"], ["zoom"], 11.5, 0.52, 16, 0.95] },
        }, beforeLayer);
        activeMap.addLayer({
          id: "thera-contours-index", type: "line", source: "thera-contours", "source-layer": "contours", minzoom: 10,
          filter: ["any", ["==", ["get", "class"], "index"], ["==", ["get", "interval"], 100]],
          layout: { visibility: "visible", "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#59442f", "line-opacity": 0.82, "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.82, 16, 1.28] },
        }, beforeLayer);
        activeMap.addLayer({
          id: "thera-contours-labels", type: "symbol", source: "thera-contours", "source-layer": "contours", minzoom: 12,
          filter: ["any", ["==", ["get", "class"], "index"], ["==", ["get", "interval"], 100]],
          layout: { visibility: "visible", "symbol-placement": "line", "symbol-spacing": 520, "text-field": ["concat", ["to-string", ["round", ["get", "ele"]]], " m"], "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"], "text-size": ["interpolate", ["linear"], ["zoom"], 12, 8.5, 16, 10.5], "text-rotation-alignment": "map", "text-max-angle": 30 },
          paint: { "text-color": "#594532", "text-halo-color": "rgba(248, 243, 229, 0.95)", "text-halo-width": 1.35 },
        }, beforeLayer);
      }

      setMessage("");
      return true;
    } catch (error) {
      if (nationalWebContours) {
        console.warn("THERA: las curvas nacionales web no pudieron abrirse.", error);
        setMessage("No fue posible abrir las curvas nacionales en línea. El mapa y el relieve continúan disponibles.");
      } else {
        console.warn("THERA: el paquete local de curvas no pudo abrirse.", error);
        setMessage("El paquete local de curvas está dañado. Elimina y vuelve a descargar esta región.");
      }
      window.setTimeout(() => setMessage(""), 4200);
      return false;
    }
  };

  const selectLayerMode = async (nextMode: LayerMode) => {
    const activeMap = mapRef.current;
    if (!activeMap || terrainLoading) return;
    setLayerMenuOpen(false);
    const regional3D = isTheraRegional3DFile(file);
    const nationalWeb = isEcuadorEssentialWeb(file);

    // Seguridad: el modo Outdoor nacional nunca se ejecuta en Android/Capacitor.
    if (nextMode === "outdoor" && !nationalWeb) return;

    if (nextMode === "map") {
      activeMap.setTerrain(null);
      if (activeMap.getLayer("thera-hillshade")) activeMap.setLayoutProperty("thera-hillshade", "visibility", "none");
      if (activeMap.getLayer("thera-color-relief")) activeMap.setLayoutProperty("thera-color-relief", "visibility", "none");
      setContourVisibility(false);
      if (regional3D) {
        applyRegionalVectorProfile(activeMap, "map");
        applyRegionalContourProfile(activeMap, "map");
      } else if (nationalWeb) {
        restoreNationalVectorProfile(activeMap);
      }
      activeMap.easeTo({ pitch: 0, bearing: 0, duration: 550 });
      setLayerMode("map");
      return;
    }

    const reliefFile = terrainFileFor(file);
    if (!reliefFile) {
      setMessage("El relieve todavía no está disponible para esta región.");
      window.setTimeout(() => setMessage(""), 2600);
      return;
    }

    setTerrainLoading(true);
    try {
      if (!terrainArchiveKey.current || !activeMap.getSource("thera-dem")) {
        const stored = await getStoredMapPack(reliefFile).catch(() => undefined);
        let terrainArchive: PMTiles;
        if (stored?.blob) {
          const terrainBytes = await Promise.race([
            stored.blob.arrayBuffer(),
            new Promise<ArrayBuffer>((_resolve, reject) =>
              window.setTimeout(() => reject(new Error("TERRAIN_BLOB_TIMEOUT")), 12000),
            ),
          ]);
          const localFile = new File([terrainBytes], reliefFile, { type: "application/vnd.pmtiles" });
          terrainArchive = new PMTiles(new FileSource(localFile));
        } else if (allowGuestViewer && !persistDownloadedMaps) {
          setMessage("Abriendo relieve en línea…");
          terrainArchive = new PMTiles(remoteMapUrl(reliefFile));
        } else {
          const token = await getAccessToken();
          if (!token && !allowGuestViewer) throw new Error("AUTH_REQUIRED");
          setMessage("Descargando relieve seguro… 0%");
          const downloaded = await downloadMapPack(reliefFile, (progress) => {
            if (activeFile.current === file) setMessage(`Descargando relieve seguro… ${progress}%`);
          }, { allowGuest: allowGuestViewer, persist: persistDownloadedMaps });
          if (downloaded.blob.size < 127) throw new Error("MAP_NOT_STORED");
          const terrainBytes = await downloaded.blob.arrayBuffer();
          const localFile = new File([terrainBytes], reliefFile, { type: "application/vnd.pmtiles" });
          terrainArchive = new PMTiles(new FileSource(localFile));
        }
        if (activeFile.current !== file || mapRef.current !== activeMap) { setMessage(""); return; }
        setMessage("Preparando relieve…");
        const terrainHeader = await readHeader(terrainArchive);
        if (activeFile.current !== file || mapRef.current !== activeMap) { setMessage(""); return; }
        const key = `terrain-${++archiveSequence}`;
        archives.set(key, terrainArchive);
        terrainArchiveKey.current = key;
        activeMap.addSource("thera-dem", {
          type: "raster-dem",
          tiles: [`thera://${key}/{z}/{x}/{y}`],
          tileSize: 256,
          encoding: "terrarium",
          minzoom: terrainHeader.minZoom,
          maxzoom: terrainHeader.maxZoom,
          bounds: [terrainHeader.minLon, terrainHeader.minLat, terrainHeader.maxLon, terrainHeader.maxLat],
          attribution: "Mapzen Terrain Tiles · AWS Open Data",
        });
        activeMap.addLayer({
          id: "thera-hillshade",
          type: "hillshade",
          source: "thera-dem",
          layout: { visibility: "none" },
          paint: { ...NATURAL_HILLSHADE },
        }, "water");

        try {
          activeMap.addLayer({
            id: "thera-color-relief",
            type: "color-relief",
            source: "thera-dem",
            layout: { visibility: "none" },
            paint: TOPO_COLOR_RELIEF_PAINT,
          } as any, "water");
        } catch (colorReliefError) {
          console.warn("THERA: color-relief no disponible en esta version de MapLibre.", colorReliefError);
        }
      }

      activeMap.setLayoutProperty("thera-hillshade", "visibility", "visible");
      const colorReliefAvailable = Boolean(activeMap.getLayer("thera-color-relief"));

      const hillshadePaint = regional3D
        ? (nextMode === "relief" ? TOPO_HILLSHADE : nextMode === "3d" ? OUTDOOR_HILLSHADE : NATURAL_HILLSHADE)
        : (nationalWeb && nextMode === "outdoor" ? OUTDOOR_HILLSHADE : nextMode === "relief" ? TOPO_HILLSHADE : NATURAL_HILLSHADE);
      activeMap.setPaintProperty("thera-hillshade", "hillshade-exaggeration", hillshadePaint["hillshade-exaggeration"]);
      activeMap.setPaintProperty("thera-hillshade", "hillshade-shadow-color", hillshadePaint["hillshade-shadow-color"]);
      activeMap.setPaintProperty("thera-hillshade", "hillshade-highlight-color", hillshadePaint["hillshade-highlight-color"]);
      activeMap.setPaintProperty("thera-hillshade", "hillshade-accent-color", hillshadePaint["hillshade-accent-color"]);

      if (colorReliefAvailable) {
        const colorPaint = regional3D
          ? (nextMode === "natural" ? NATURAL_COLOR_RELIEF_PAINT : nextMode === "3d" ? OUTDOOR_COLOR_RELIEF_PAINT : TOPO_COLOR_RELIEF_PAINT)
          : (nationalWeb && nextMode === "outdoor" ? OUTDOOR_COLOR_RELIEF_PAINT : TOPO_COLOR_RELIEF_PAINT);
        safePaint(activeMap, "thera-color-relief", "color-relief-color", colorPaint["color-relief-color"]);
        safePaint(activeMap, "thera-color-relief", "color-relief-opacity", colorPaint["color-relief-opacity"]);
        activeMap.setLayoutProperty("thera-color-relief", "visibility", regional3D || nextMode === "relief" || (nationalWeb && nextMode === "outdoor") ? "visible" : "none");
      }

      if (regional3D) {
        applyRegionalVectorProfile(activeMap, nextMode);
      } else if (nationalWeb && nextMode === "outdoor") {
        // Mismo perfil visual Outdoor regional, aplicado solo al visor nacional web.
        applyRegionalVectorProfile(activeMap, "3d");
      } else if (nationalWeb) {
        restoreNationalVectorProfile(activeMap);
      }

      // Curvas: en Ecuador esencial WEB se fuerzan al entrar a Relieve y se espera
      // su registro antes de finalizar el cambio de capa. Android/regionales
      // conservan exactamente el flujo existente.
      const nationalWebContoursMode =
        nationalWeb && (nextMode === "relief" || nextMode === "outdoor");

      if (nationalWebContoursMode) {
        const ok = await ensureContourLayers(activeMap);
        if (mapRef.current === activeMap && activeFile.current === file) {
          setContourVisibility(ok);
          applyNationalWebContourProfile(activeMap, nextMode === "outdoor");
        }
      } else if (nextMode === "relief" || (regional3D && nextMode === "3d")) {
        void ensureContourLayers(activeMap)
          .then((ok) => {
            if (mapRef.current === activeMap && activeFile.current === file) {
              setContourVisibility(ok);
              if (regional3D) applyRegionalContourProfile(activeMap, nextMode);
            }
          })
          .catch((error) => console.warn("THERA: curvas opcionales no bloquearon el relieve.", error));
      } else {
        setContourVisibility(false);
        if (regional3D) applyRegionalContourProfile(activeMap, nextMode);
      }

      if (regional3D) {
        if (nextMode === "natural") {
          activeMap.setTerrain({ source: "thera-dem", exaggeration: 1.08 });
          activeMap.easeTo({ pitch: 48, bearing: -10, duration: 700 });
        } else if (nextMode === "relief") {
          activeMap.setTerrain({ source: "thera-dem", exaggeration: 1.12 });
          activeMap.easeTo({ pitch: 54, bearing: -14, duration: 720 });
        } else {
          activeMap.setTerrain({ source: "thera-dem", exaggeration: 1.12 });
          activeMap.easeTo({ pitch: 54, bearing: -14, duration: 740 });
        }
      } else if (nationalWeb && nextMode === "outdoor") {
        // Replica el encuadre Outdoor regional sin modificar el modo 3D nacional existente.
        activeMap.setTerrain({ source: "thera-dem", exaggeration: 1.12 });
        activeMap.easeTo({ pitch: 54, bearing: -14, duration: 740 });
      } else if (nextMode === "3d") {
        activeMap.setTerrain({ source: "thera-dem", exaggeration: 1.22 });
        activeMap.easeTo({ pitch: 62, bearing: -18, duration: 750 });
      } else {
        activeMap.setTerrain({ source: "thera-dem", exaggeration: 1.14 });
        activeMap.easeTo({ pitch: 55, bearing: -14, duration: 700 });
      }

      setLayerMode(nextMode);
      setMessage("");
      if (!colorReliefAvailable && (nextMode === "relief" || regional3D || (nationalWeb && nextMode === "outdoor"))) {
        setMessage("Vista 3D activa; el color por elevación requiere una versión compatible de MapLibre.");
        window.setTimeout(() => setMessage(""), 3200);
      }
    } catch (error: unknown) {
      const detail = error instanceof Error ? error.message : "";
      setMessage(detail === "AUTH_REQUIRED"
        ? "Inicia sesión con una cuenta aprobada para descargar el relieve."
        : detail.includes("404")
          ? "El archivo de relieve todavía no está disponible en R2."
          : detail === "TERRAIN_BLOB_TIMEOUT" || detail === "MAP_TIMEOUT"
            ? "El relieve tardó demasiado en prepararse. El mapa base continúa disponible."
            : "No fue posible abrir el relieve de esta región.");
    } finally {
      setTerrainLoading(false);
    }
  };

  useEffect(() => {
    if (!container.current) return;
    let map: maplibregl.Map | null = null;
    let cancelled = false;
    let loadingTimer: number | undefined;
    let archiveKey: string | undefined;
    let initialMapReady = false;
    const start = async () => {
      setLayerMode("map");
      setLayerMenuOpen(false);
      contourArchiveKey.current = undefined;
      if (derivedOverlayArchiveKey.current) { archives.delete(derivedOverlayArchiveKey.current); derivedOverlayArchiveKey.current = undefined; }
      setDerivedOverlayEnabled(false);
      setDerivedOverlayReleaseLabel(null);
      const token = await getAccessToken();
      registerProtocol();
      const stored = await getStoredMapPack(file).catch(() => undefined);
      let archive: PMTiles;
      if (stored?.blob) {
        // Materializar el Blob evita que Chrome Android pierda el manejador
        // interno de IndexedDB después de cerrar por completo el navegador.
        const mapBytes = await stored.blob.arrayBuffer();
        const localFile = new File([mapBytes], file, { type: "application/vnd.pmtiles" });
        archive = new PMTiles(new FileSource(localFile));
      } else if (allowGuestViewer && !persistDownloadedMaps) {
        // El visor público usa Range requests: abrir un paquete nacional no
        // implica descargarlo completo ni ocupar la memoria del teléfono.
        setMessage("Abriendo mapa en línea…");
        archive = new PMTiles(remoteMapUrl(file));
      } else {
        if (!token && !allowGuestViewer) { setMessage("Inicia sesión con una cuenta aprobada para abrir este mapa."); return; }
        setMessage(allowGuestViewer ? "Abriendo mapa para visitante… 0%" : "Descargando mapa seguro… 0%");
        const downloaded = await downloadMapPack(file, (progress) => {
          if (!cancelled) setMessage(allowGuestViewer
            ? `Abriendo mapa para visitante… ${progress}%`
            : `Descargando mapa seguro… ${progress}%`);
        }, { allowGuest: allowGuestViewer, persist: persistDownloadedMaps });
        if (downloaded.blob.size < 127) throw new Error("MAP_NOT_STORED");
        const mapBytes = await downloaded.blob.arrayBuffer();
        const localFile = new File([mapBytes], file, { type: "application/vnd.pmtiles" });
        archive = new PMTiles(new FileSource(localFile));
      }
      setMessage("Preparando mapa seguro…");
      const header = await readHeader(archive);
      archiveKey = `map-${++archiveSequence}`;
      archives.set(archiveKey, archive);
      if (cancelled || !container.current) return;
      map = new maplibregl.Map({
        container: container.current,
        style: theraStyle(archiveKey, header, isTheraRegional3DFile(file)),
        center,
        zoom,
        renderWorldCopies: false,
        maxPitch: 78,
        attributionControl: false,
        canvasContextAttributes: { preserveDrawingBuffer: true },
      });
      mapRef.current = map;
      map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
      // Ubicarme es una función básica del mapa, no inicia ni guarda una ruta.
      // Debe permanecer visible también para visitantes y en la app Android.
      map.addControl(new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true, timeout: 15_000 },
        fitBoundsOptions: { maxZoom: 15 },
        trackUserLocation: true,
        // Mantener visible el indicador GPS básico también en Explorar.
        // El marcador avanzado de navegación sigue usando currentPosition
        // cuando existe, pero este punto azul no depende de iniciar una ruta.
        showUserLocation: true,
        showAccuracyCircle: true,
      }), "top-right");
      map.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: "THERA" }));
      loadingTimer = window.setTimeout(() => {
        setMessage((current) => current ? "El mapa tardó demasiado. Recarga la página para volver a intentarlo." : current);
      }, 15000);
      map.on("load", () => {
        initialMapReady = true;
        if (loadingTimer !== undefined) window.clearTimeout(loadingTimer);
        const loadedMap = map;
        if (!loadedMap) return;
        const routeLine = lineFeature(routeGeojson);
        const coordinates = routeLine?.geometry.type === "LineString" ? routeLine.geometry.coordinates : [];
        if (coordinates.length > 1) {
          loadedMap.addSource("thera-route", { type: "geojson", data: routeGeojson! });
          loadedMap.addLayer({
            id: "thera-route-shadow",
            type: "line",
            source: "thera-route",
            paint: { "line-color": "#1b3148", "line-width": 9, "line-opacity": 0.3 },
            layout: { "line-cap": "round", "line-join": "round" },
          });
          const explicitWaypoints = routeGeojson!.features.filter((feature) => feature.geometry.type === "Point");
          const waypointData = {
            type: "FeatureCollection" as const,
            features: [
              {
                type: "Feature" as const,
                properties: { id: "route-start", name: "Inicio", kind: "start" },
                geometry: { type: "Point" as const, coordinates: coordinates[0] },
              },
              ...explicitWaypoints,
              {
                type: "Feature" as const,
                properties: { id: "route-finish", name: "Final", kind: "finish" },
                geometry: { type: "Point" as const, coordinates: coordinates.at(-1)! },
              },
            ],
          };
          loadedMap.addSource("thera-route-waypoints", { type: "geojson", data: waypointData });
          loadedMap.addLayer({
            id: "thera-route-waypoint-dots",
            type: "circle",
            source: "thera-route-waypoints",
            paint: {
              "circle-radius": ["case", ["in", ["get", "kind"], ["literal", ["start", "finish"]]], 7, 5],
              "circle-color": ["match", ["get", "kind"], "start", "#268a57", "finish", "#d04a32", "danger", "#d04a32", "water", "#278ea8", "#ff7f00"],
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });
          loadedMap.addLayer({
            id: "thera-route-waypoint-labels",
            type: "symbol",
            source: "thera-route-waypoints",
            minzoom: 11,
            layout: {
              "text-field": ["get", "name"],
              "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
              "text-size": 12,
              "text-offset": [0, 1.1],
              "text-anchor": "top",
              "text-allow-overlap": false,
            },
            paint: { "text-color": "#132f48", "text-halo-color": "#ffffff", "text-halo-width": 2 },
          });
          loadedMap.addLayer({
            id: "thera-route-line",
            type: "line",
            source: "thera-route",
            paint: { "line-color": "#ff7f00", "line-width": 5 },
            layout: { "line-cap": "round", "line-join": "round" },
          });
          const bounds = coordinates.reduce(
            (current, coordinate) => current.extend([coordinate[0], coordinate[1]]),
            new maplibregl.LngLatBounds(
              [coordinates[0][0], coordinates[0][1]],
              [coordinates[0][0], coordinates[0][1]],
            ),
          );
          loadedMap.fitBounds(bounds, { padding: 55, maxZoom: 15, duration: 0 });
        }
        loadedMap.addSource("thera-planner-route", { type: "geojson", data: plannerRouteFeature(plannerRouteCoordinates) });
        loadedMap.addLayer({
          id: "thera-planner-route-shadow",
          type: "line",
          source: "thera-planner-route",
          paint: { "line-color": "#ffffff", "line-width": 8, "line-opacity": 0.9 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addLayer({
          id: "thera-planner-route-line",
          type: "line",
          source: "thera-planner-route",
          paint: {
            "line-color": plannerRouteHasFallback ? "#ff9f45" : "#ff7f00",
            "line-width": 5,
            "line-dasharray": plannerRouteHasFallback ? [1.5, 1.2] : [1, 0],
          },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addSource("thera-planner-points", { type: "geojson", data: plannerPointsFeature(plannerPoints) });
        loadedMap.addLayer({
          id: "thera-planner-point-dots",
          type: "circle",
          source: "thera-planner-points",
          paint: {
            "circle-radius": ["match", ["get", "kind"], "start", 7, "finish", 7, 6],
            "circle-color": ["match", ["get", "kind"], "start", "#268a57", "finish", "#d04a32", "#ff7f00"],
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 2,
          },
        });
        loadedMap.addLayer({
          id: "thera-planner-point-labels",
          type: "symbol",
          source: "thera-planner-points",
          layout: {
            "text-field": ["get", "name"],
            "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
            "text-size": 12,
            "text-offset": [0, 1.1],
            "text-anchor": "top",
            "text-allow-overlap": false,
          },
          paint: { "text-color": "#132f48", "text-halo-color": "#ffffff", "text-halo-width": 2 },
        });
        if (plannerActive) {
          loadedMap.getCanvas().style.cursor = "crosshair";
          if (onPlannerMapClick) {
            loadedMap.on("click", (event) => {
              onPlannerMapClick({ latitude: event.lngLat.lat, longitude: event.lngLat.lng });
            });
          }
        }

        loadedMap.addSource("thera-recorded-track", { type: "geojson", data: recordedTrackFeature(recordedTrack) });
        loadedMap.addSource("thera-route-approach", { type: "geojson", data: approachLineFeature(currentPosition, approachTarget) });
        loadedMap.addLayer({
          id: "thera-route-approach-shadow",
          type: "line",
          source: "thera-route-approach",
          paint: { "line-color": "#ffffff", "line-width": 8, "line-opacity": 0.92 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addSource("thera-road-approach", { type: "geojson", data: roadApproachFeature(roadApproachCoordinates) });
        loadedMap.addLayer({
          id: "thera-road-approach-shadow",
          type: "line",
          source: "thera-road-approach",
          paint: { "line-color": "#102f46", "line-width": 9, "line-opacity": 0.35 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addLayer({
          id: "thera-road-approach-line",
          type: "line",
          source: "thera-road-approach",
          paint: { "line-color": "#00a6c8", "line-width": 5.2, "line-opacity": 0.96 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addLayer({
          id: "thera-route-approach-line",
          type: "line",
          source: "thera-route-approach",
          paint: { "line-color": "#167ca4", "line-width": 4, "line-dasharray": [1.4, 1.2] },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addLayer({
          id: "thera-recorded-track-shadow",
          type: "line",
          source: "thera-recorded-track",
          paint: { "line-color": "#ffffff", "line-width": 8, "line-opacity": 0.86 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addLayer({
          id: "thera-recorded-track-line",
          type: "line",
          source: "thera-recorded-track",
          paint: { "line-color": "#167ca4", "line-width": 4.5 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        loadedMap.addSource("thera-search-target", { type: "geojson", data: searchTargetFeature(searchTarget) });
        loadedMap.addLayer({
          id: "thera-search-target-halo",
          type: "circle",
          source: "thera-search-target",
          paint: {
            "circle-radius": 15,
            "circle-color": "rgba(255,127,0,.22)",
            "circle-stroke-color": "rgba(255,255,255,.92)",
            "circle-stroke-width": 2,
          },
        });
        loadedMap.addLayer({
          id: "thera-search-target-dot",
          type: "circle",
          source: "thera-search-target",
          paint: {
            "circle-radius": 7,
            "circle-color": "#ff7f00",
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 2.5,
          },
        });
        loadedMap.addLayer({
          id: "thera-search-target-label",
          type: "symbol",
          source: "thera-search-target",
          layout: {
            "text-field": ["get", "name"],
            "text-font": ["Arial", "Helvetica", "Noto Sans", "sans-serif"],
            "text-size": 12,
            "text-offset": [0, 1.5],
            "text-anchor": "top",
            "text-max-width": 14,
          },
          paint: { "text-color": "#132f48", "text-halo-color": "#ffffff", "text-halo-width": 2 },
        });
        if (searchTarget && coordinates.length < 2) {
          loadedMap.easeTo({
            center: [searchTarget.longitude, searchTarget.latitude],
            zoom: Math.max(loadedMap.getZoom(), 15),
            duration: 0,
          });
        }
        loadedMap.addSource("thera-current-position", { type: "geojson", data: currentPositionFeature(currentPosition, currentHeadingDegrees) });
        loadedMap.addLayer({
          id: "thera-current-position-halo",
          type: "circle",
          source: "thera-current-position",
          paint: { "circle-radius": 12, "circle-color": "rgba(22,124,164,.24)", "circle-stroke-color": "rgba(255,255,255,.8)", "circle-stroke-width": 1 },
        });
        if (currentPosition && followUser) {
          if (approachTarget) {
            loadedMap.fitBounds(
              new maplibregl.LngLatBounds([currentPosition.longitude, currentPosition.latitude], approachTarget),
              { padding: 72, maxZoom: 14, duration: 0 },
            );
          } else {
            loadedMap.easeTo({ center: [currentPosition.longitude, currentPosition.latitude], zoom: Math.max(loadedMap.getZoom(), 15), duration: 0 });
          }
        }
        loadedMap.addLayer({
          id: "thera-current-position-dot",
          type: "symbol",
          source: "thera-current-position",
          layout: {
            "text-field": "▲",
            "text-size": 24,
            "text-rotate": ["get", "heading"],
            "text-rotation-alignment": "map",
            "text-allow-overlap": true,
            "text-ignore-placement": true,
          },
          paint: {
            "text-color": "#167ca4",
            "text-halo-color": "#ffffff",
            "text-halo-width": 2.5,
          },
        });
        onSnapshotProvider?.(() => {
          try {
            return loadedMap.getCanvas().toDataURL("image/png");
          } catch {
            return null;
          }
        });
        setMessage("");
      });
      map.on("error", (event) => {
        const detail = event.error?.message ?? "";
        // MapLibre puede informar fallos aislados de una tesela mientras el
        // mapa y el relieve continúan funcionando. Después de la carga inicial
        // esos avisos son recuperables y no deben cubrir el visor con un error.
        if (initialMapReady) {
          console.warn("THERA recoverable map tile warning", event.error);
          return;
        }
        if (loadingTimer !== undefined) window.clearTimeout(loadingTimer);
        console.error("THERA map initialization error", event.error);
        setMessage(detail.includes("401")
          ? "Tu sesión no fue autorizada para abrir este mapa."
          : "No fue posible representar este mapa.");
      });
    };
    start().catch((error: unknown) => {
      const detail = error instanceof Error ? error.message : "";
      setMessage(detail === "MAP_TIMEOUT"
        ? "La conexión con el mapa superó los 15 segundos. Recarga para intentarlo nuevamente."
        : detail.includes("401")
          ? "Tu sesión no fue autorizada para abrir este mapa."
          : detail.includes("404")
            ? "Este paquete regional todavía no está disponible."
            : "No fue posible abrir el mapa regional.");
    });
    return () => {
      cancelled = true;
      if (loadingTimer !== undefined) window.clearTimeout(loadingTimer);
      mapRef.current = null;
      onSnapshotProvider?.(null);
      map?.remove();
      if (archiveKey) archives.delete(archiveKey);
      if (terrainArchiveKey.current) {
        archives.delete(terrainArchiveKey.current);
        terrainArchiveKey.current = undefined;
      }
      if (contourArchiveKey.current) {
        archives.delete(contourArchiveKey.current);
        contourWebArchives.delete(contourArchiveKey.current);
        contourArchiveKey.current = undefined;
      }
      if (derivedOverlayArchiveKey.current) {
        archives.delete(derivedOverlayArchiveKey.current);
        derivedOverlayArchiveKey.current = undefined;
      }
    };
  }, [file, center[0], center[1], zoom, routeGeojson, allowGuestViewer, persistDownloadedMaps, onSnapshotProvider]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const routeSource = map.getSource("thera-planner-route") as maplibregl.GeoJSONSource | undefined;
    routeSource?.setData(plannerRouteFeature(plannerRouteCoordinates));
    const pointsSource = map.getSource("thera-planner-points") as maplibregl.GeoJSONSource | undefined;
    pointsSource?.setData(plannerPointsFeature(plannerPoints));
    if (map.getLayer("thera-planner-route-line")) {
      map.setPaintProperty("thera-planner-route-line", "line-color", plannerRouteHasFallback ? "#ff9f45" : "#ff7f00");
      map.setPaintProperty("thera-planner-route-line", "line-dasharray", plannerRouteHasFallback ? [1.5, 1.2] : [1, 0]);
    }
  }, [plannerPoints, plannerRouteCoordinates, plannerRouteHasFallback]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    map.getCanvas().style.cursor = plannerActive ? "crosshair" : "";
    if (!plannerActive || !onPlannerMapClick) return;
    const handleClick = (event: maplibregl.MapMouseEvent) => {
      onPlannerMapClick({ latitude: event.lngLat.lat, longitude: event.lngLat.lng });
    };
    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
      if (map.getCanvas()) map.getCanvas().style.cursor = "";
    };
  }, [plannerActive, onPlannerMapClick]);

  useEffect(() => {
    // THERA_MI_MAPA_CLEAN_V1
    // Único punto donde Mi mapa crea su source y layer.
    // Se ejecuta después de que el visor principal termina su carga.
    if (!personalMapGeojson) return;

    let cancelled = false;
    let retryTimer: number | undefined;
    let attempts = 0;

    const syncPersonalMap = () => {
      if (cancelled) return;

      const map = mapRef.current;

      // Esperar hasta que el mapa normal haya terminado completamente.
      if (!map || message !== "" || !map.isStyleLoaded()) {
        attempts += 1;

        if (attempts <= 400) {
          retryTimer = window.setTimeout(syncPersonalMap, 50);
        }

        return;
      }

      try {
        let source = map.getSource("thera-personal-map") as
          | maplibregl.GeoJSONSource
          | undefined;

        if (!source) {
          map.addSource("thera-personal-map", {
            type: "geojson",
            data: personalMapGeojson,
          });

          map.addLayer({
            id: "thera-personal-map-line",
            type: "line",
            source: "thera-personal-map",
            filter: ["==", ["geometry-type"], "LineString"],
            paint: {
              "line-color": "#ff7f00",
              "line-width": [
                "case",
                ["==", ["get", "selected"], true],
                6,
                4,
              ],
              "line-opacity": 0.95,
            },
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
          });

          source = map.getSource("thera-personal-map") as
            | maplibregl.GeoJSONSource
            | undefined;
        }

        source?.setData(personalMapGeojson);
        map.triggerRepaint();
      } catch (error) {
        console.warn("THERA Mi mapa overlay warning", error);
      }
    };

    syncPersonalMap();

    return () => {
      cancelled = true;

      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [personalMapGeojson, message, file]);

  useEffect(() => {
    if (!personalMapFocusBounds) return;

    let cancelled = false;
    let retryTimer: number | undefined;
    let attempts = 0;

    const focusPersonalMap = () => {
      if (cancelled) return;

      const map = mapRef.current;

      if (!map || message !== "" || !map.isStyleLoaded()) {
        attempts += 1;

        if (attempts <= 400) {
          retryTimer = window.setTimeout(focusPersonalMap, 50);
        }

        return;
      }

      map.fitBounds(
        [
          [personalMapFocusBounds.west, personalMapFocusBounds.south],
          [personalMapFocusBounds.east, personalMapFocusBounds.north],
        ],
        {
          padding: 64,
          maxZoom: 15,
          duration: 500,
        },
      );
    };

    focusPersonalMap();

    return () => {
      cancelled = true;

      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [personalMapFocusBounds, message, file]);

  useEffect(() => {
    if (!onPersonalMapTrackSelect) return;

    let cancelled = false;
    let retryTimer: number | undefined;
    let attempts = 0;
    let boundMap: maplibregl.Map | null = null;

    const bindPersonalMapClick = () => {
      if (cancelled) return;

      const map = mapRef.current;

      if (
        !map ||
        message !== "" ||
        !map.isStyleLoaded() ||
        !map.getLayer("thera-personal-map-line")
      ) {
        attempts += 1;

        if (attempts <= 400) {
          retryTimer = window.setTimeout(bindPersonalMapClick, 50);
        }

        return;
      }

      boundMap = map;

      const handlePersonalMapClick = (
        event: maplibregl.MapMouseEvent,
      ) => {
        if (!map.getLayer("thera-personal-map-line")) return;

        const features = map.queryRenderedFeatures(
          event.point,
          {
            layers: ["thera-personal-map-line"],
          },
        );

        const activityId =
          features[0]?.properties?.activity_id;

        onPersonalMapTrackSelect(
          typeof activityId === "string"
            ? activityId
            : null,
        );
      };

      map.on("click", handlePersonalMapClick);

      return () => {
        map.off("click", handlePersonalMapClick);
      };
    };

    let unbind: (() => void) | undefined;

    const start = () => {
      const result = bindPersonalMapClick();

      if (typeof result === "function") {
        unbind = result;
      }
    };

    start();

    return () => {
      cancelled = true;

      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }

      unbind?.();

      boundMap = null;
    };
  }, [onPersonalMapTrackSelect, message, file]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource("thera-recorded-track") as maplibregl.GeoJSONSource | undefined;
    source?.setData(recordedTrackFeature(recordedTrack));
  }, [recordedTrack]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource("thera-search-target") as maplibregl.GeoJSONSource | undefined;
    source?.setData(searchTargetFeature(searchTarget));
    if (searchTarget) {
      map.easeTo({
        center: [searchTarget.longitude, searchTarget.latitude],
        zoom: Math.max(map.getZoom(), 15),
        duration: 650,
      });
    }
  }, [searchTarget]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource("thera-road-approach") as maplibregl.GeoJSONSource | undefined;
    source?.setData(roadApproachFeature(roadApproachCoordinates));
  }, [roadApproachCoordinates]);

  useEffect(() => {
    const map = mapRef.current;
    const signal = recenterSignal ?? recentreSignal;
    if (!map?.isStyleLoaded() || !currentPosition || signal === undefined) return;
    map.easeTo({
      center: [currentPosition.longitude, currentPosition.latitude],
      zoom: Math.max(map.getZoom(), 15),
      duration: 450,
    });
  }, [recenterSignal, recentreSignal, currentPosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource("thera-current-position") as maplibregl.GeoJSONSource | undefined;
    source?.setData(currentPositionFeature(currentPosition, currentHeadingDegrees));
    const approachSource = map.getSource("thera-route-approach") as maplibregl.GeoJSONSource | undefined;
    approachSource?.setData(approachLineFeature(currentPosition, approachTarget));
    if (currentPosition && followUser) {
      if (approachTarget && roadApproachCoordinates && roadApproachCoordinates.length > 1) {
        const bounds = roadApproachCoordinates.reduce(
          (current, coordinate) => current.extend([coordinate[0], coordinate[1]]),
          new maplibregl.LngLatBounds(
            [currentPosition.longitude, currentPosition.latitude],
            [currentPosition.longitude, currentPosition.latitude],
          ),
        );
        map.fitBounds(bounds, { padding: 72, maxZoom: 15, duration: 450 });
      } else if (approachTarget) {
        map.fitBounds(
          new maplibregl.LngLatBounds([currentPosition.longitude, currentPosition.latitude], approachTarget),
          { padding: 72, maxZoom: 14, duration: 450 },
        );
      } else {
        map.easeTo({ center: [currentPosition.longitude, currentPosition.latitude], duration: 450, zoom: Math.max(map.getZoom(), 15) });
      }
    }
  }, [currentPosition, currentHeadingDegrees, approachTarget, roadApproachCoordinates, followUser]);

  return (
    <div className="real-map">
      <div ref={container} className="real-map-canvas" />
      <div className="map-layer-control">
        <button
          type="button"
          className="map-layer-trigger"
          onClick={() => setLayerMenuOpen((current) => !current)}
          aria-expanded={layerMenuOpen}
          disabled={Boolean(message) || terrainLoading}
        >
          <Layers3 /> Capas
        </button>
        {layerMenuOpen && (
          <div className="map-layer-menu" role="radiogroup" aria-label="Vista del mapa">
            <button type="button" className={layerMode === "map" ? "selected" : ""} onClick={() => selectLayerMode("map")} aria-checked={layerMode === "map"} role="radio">
              <MapIcon /><span><strong>Mapa</strong><small>Vista rápida · máxima ligereza</small></span>
            </button>
            {isTheraRegional3DFile(file) && (
              <button type="button" className={layerMode === "natural" ? "selected" : ""} onClick={() => selectLayerMode("natural")} aria-checked={layerMode === "natural"} role="radio">
                <Mountain /><span><strong>Natural 3D</strong><small>Terreno orgánico · sombras naturales</small></span>
              </button>
            )}
            <button type="button" className={layerMode === "relief" ? "selected" : ""} onClick={() => selectLayerMode("relief")} aria-checked={layerMode === "relief"} role="radio">
              <Mountain /><span><strong>{isTheraRegional3DFile(file) ? "Topográfico 3D" : "Relieve"}</strong><small>Curvas · cotas · elevación real</small></span>
            </button>
            {isEcuadorEssentialWeb(file) && (
              <button type="button" className={layerMode === "outdoor" ? "selected" : ""} onClick={() => selectLayerMode("outdoor")} aria-checked={layerMode === "outdoor"} role="radio">
                <Layers3 /><span><strong>Outdoor 3D</strong><small>Natural + topografía equilibrada · solo web</small></span>
              </button>
            )}
            <button type="button" className={layerMode === "3d" ? "selected" : ""} onClick={() => selectLayerMode("3d")} aria-checked={layerMode === "3d"} role="radio">
              <Layers3 /><span><strong>{isTheraRegional3DFile(file) ? "Outdoor 3D" : "3D"}</strong><small>{isTheraRegional3DFile(file) ? "Natural + topografía equilibrada" : "Inclinar y rotar"}</small></span>
            </button>
            {THERA_DERIVED_LAYER_RENDERING_ENABLED && derivedOverlayAdmin && (
              <button
                type="button"
                className={derivedOverlayEnabled ? "selected" : ""}
                onClick={() => void toggleDerivedFieldOverlay()}
                disabled={derivedOverlayBusy}
                aria-pressed={derivedOverlayEnabled}
              >
                <Layers3 />
                <span>
                  <strong>{derivedOverlayEnabled
                    ? "Field Survey V1 · visible"
                    : derivedOfflineRuntime
                      ? derivedOverlayOfflineStored ? "Field Survey V1 · offline" : "Field Survey V1 · descargar"
                      : "Field Survey V1"}</strong>
                  <small>{derivedOverlayBusy
                    ? derivedOverlayDownloadProgress !== null
                      ? `Descargando capa derivada… ${derivedOverlayDownloadProgress}%`
                      : "Preparando capa derivada…"
                    : derivedOverlayReleaseLabel
                      ?? (derivedOfflineRuntime
                        ? derivedOverlayOfflineStored ? "Guardado en este dispositivo" : "Descarga admin · uso sin conexión"
                        : "Overlay piloto · solo admin Preview")}</small>
                </span>
              </button>
            )}
            <p className="map-layer-note">{isTheraRegional3DFile(file) ? "THERA MAP ENGINE · Región 3D. Vías integradas, relieve y curvas adaptativas por zoom. Todo funciona sin conexión." : isEcuadorEssentialWeb(file) ? "Outdoor 3D nacional se activa solo en web. Android conserva sin cambios sus mapas regionales y modo offline." : "Los nombres aparecen al acercar y funcionan también sin conexión."}</p>
          </div>
        )}
      </div>
      {message && <div className="real-map-message">{message}</div>}
      <div className="map-attribution">© OpenStreetMap · Mapzen Terrain</div>
    </div>
  );
}
